import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useMemo } from 'react';
import { $getNodeByKey } from 'lexical';
import { useImageBox } from '@/components/ImageBox';
import { useClippoBlobs } from '@/utils';
import { ImageNode } from '../Nodes';

export default function ImageResizePlugin({ ownerId }: { ownerId?: string }) {
  const [editor] = useLexicalComposerContext();

  const imageBoxProps = useImageBox({
    noPreload: true,
    defer: false,
    ownerId: ownerId!,
    ownerType: 'Article',
  });

  const { blobs } = useClippoBlobs(imageBoxProps);

  useEffect(() => {
    if (ownerId) imageBoxProps.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const blobMap = useMemo(() => {
    if (!blobs || blobs.length === 0) return null;

    const map: Record<string, string> = {};
    for (const x of blobs) {
      if (x.id && x.blobURL) {
        map[x.id] = x.blobURL;
      }
    }

    return map;
  }, [blobs]);

  useEffect(() => {
    if (!blobMap) return;

    editor.update(() => {
      const nodeMap = editor.getEditorState()._nodeMap;

      nodeMap.forEach((node) => {
        if (node instanceof ImageNode) {
          node.setSrc(blobMap);
          node.getWritable();
        }
      });
    });
  }, [editor, blobMap]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const type = target.dataset.resizeHandle;
      if (!type) return;

      const wrapper = target.parentElement!;
      const nodeKey = wrapper.dataset.nodeKey!;
      const img = wrapper.querySelector('img')!;

      event.preventDefault();

      const startX = event.clientX;
      const startY = event.clientY;
      const startWidth = img.width;
      const startHeight = img.height;

      const onMouseMove = (e: MouseEvent) => {
        let newWidth = startWidth;
        let newHeight = startHeight;

        if (type === 'diagonal') {
          newWidth = Math.max(50, startWidth + (e.clientX - startX));
          newHeight = Math.max(50, startHeight + (e.clientY - startY));
        } else if (type === 'right') {
          newWidth = Math.max(50, startWidth + (e.clientX - startX));
        } else if (type === 'bottom') {
          newHeight = Math.max(50, startHeight + (e.clientY - startY));
        }

        img.width = newWidth;
        img.height = newHeight;

        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (!node) return;

          if ('setWidth' in node) (node as any).setWidth(newWidth);
          if ('setHeight' in node) (node as any).setHeight(newHeight);
        });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editor]);

  return null;
}
